import * as React from 'react';
import { Subtract } from 'utility-types';

import { ServiceLocator } from '../../app/ServiceLocator';
import { RouterError } from '../../app/spaces/RouterHelpers/RouterError';
import { SpacesSiteUrls } from '../../app/spaces/SpacesSiteUrls';
import { Filter } from '../../ts/helpers/Filter';
import { FlowDetailInfo, FlowListItemInfo } from '../../ts/models/FlowInfo';
import { INamedItemInfo } from '../../ts/models/resourceInfoParts';
import { Flows } from '../../ts/resources/Flows';
import { Projects } from '../../ts/resources/Projects';
import { FlowAllowedActions } from '../../ts/ServerApi/types/FlowAllowedActions';

interface InjectedFlowSpaceData {
  readonly flowAllowedActions: FlowAllowedActions;
  readonly flowDetailInfo: FlowDetailInfo;
  readonly flowListItemInfo: FlowListItemInfo;
  readonly projectHierarchy: INamedItemInfo[];
}

export interface InjectedFlowSpaceDataFetcherProps extends InjectedFlowSpaceData {
  readonly refreshData: () => void;
}

export interface FlowSpaceDataFetcherProps {
  readonly flowId: string;
  readonly siteUrls: SpacesSiteUrls;
}

export function withFlowSpaceDataFetcher<P extends InjectedFlowSpaceDataFetcherProps>(
  Component: React.ComponentType<P>
) {
  return function FlowSpaceDataFetcher(
    props: Subtract<P, InjectedFlowSpaceDataFetcherProps> & FlowSpaceDataFetcherProps
  ) {
    const [ error, setError ] = React.useState(false);
    const [ injectedData, setInjectedData ] = React.useState<InjectedFlowSpaceData>(undefined);

    let didCancel = false;

    const refreshData = () => {
      fetchFlowSpaceData(props.flowId)
        .then(injectedData => {
          !didCancel && setError(false);
          !didCancel && setInjectedData(injectedData);
        })
        .catch(() => !didCancel && setError(true));
    };

    React.useEffect(() => {
      refreshData();
      return () => didCancel = true;
    }, [ props.flowId ]);

    if (error) {
      return <RouterError errorType={'flow'} urls={props.siteUrls}/>;
    } else if (!injectedData) {
      return null;
    }

    return <Component {...props as unknown as P} {...injectedData} refreshData={refreshData}/>;
  };
}

async function fetchFlowSpaceData(flowId: string): Promise<InjectedFlowSpaceData> {
  const flows = ServiceLocator.get<Flows>('Flows');
  const flowAllowedActions = await flows.actions(flowId);
  const flowDetailInfo = await flows.getById(flowId);
  const flowListItemInfo = await flows.all()
    .withFilters([Filter.fieldEq('id', flowId)])
    .getFirst();

  const projects = ServiceLocator.get<Projects>('Projects');
  const projectHierarchy = await projects.getProjectHierarchy(flowDetailInfo.project.id);

  return {
    flowAllowedActions,
    flowDetailInfo,
    flowListItemInfo,
    projectHierarchy,
  };
}
